/**
 * Public storage images are stored as host-free references,
 * `<bucket>/<object path>[?v=<version>]` — the tail of the public URL — and
 * resolved to a URL only where they are used. Rows written before references
 * hold full URLs on the project host; every function here accepts both.
 *
 * Client-safe and pure: the env reads are literal `process.env.NEXT_PUBLIC_*`
 * accesses so Next inlines them into client bundles.
 */

import { getSupabaseProjectOrigin } from '../supabase/supabase-project';

export const PUBLIC_STORAGE_BUCKETS = [
  'account_image',
  'branding',
  'class_images',
  'facility_images',
  'home_assets',
  'membership_plan_images',
  'product_images',
  'trainer_images',
] as const;

export type PublicStorageBucket = (typeof PUBLIC_STORAGE_BUCKETS)[number];

export interface StorageRef {
  bucket: PublicStorageBucket;
  /** Object path inside the bucket, decoded, without the query. */
  path: string;
  /** The `?v=` cache-buster, when present. */
  version: string | null;
}

const PUBLIC_OBJECT_PATH = '/storage/v1/object/public/';

// The URL parser drops tabs/newlines, reads `\` as `/` and resolves `%2e%2e`,
// so any of these could carry a reference out of its bucket. A decoded path
// may contain spaces (`%20`), nothing else below 0x21.
function hasUnsafeCharacter(value: string, { allowSpace = false } = {}) {
  for (const char of value) {
    const code = char.charCodeAt(0);
    if (char === ' ' && allowSpace) continue;
    if (char === '\\' || code <= 0x20 || code === 0x7f) return true;
  }

  return false;
}

// Normalised the way supabase-js builds `getPublicUrl()` (lower-case scheme
// and host, no default port), so legacy URLs match whatever casing the env has.
function trimBase(value: string | undefined) {
  // Runs on every image render; an unset env must not cost a thrown error.
  const raw = value?.trim();
  if (!raw) return null;

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }

  return url.protocol === 'https:' || url.protocol === 'http:'
    ? `${url.origin}${url.pathname.replace(/\/+$/, '')}`
    : null;
}

/** Where public images are served from: the Supabase URL. */
export function getStoragePublicBaseUrl(): string | null {
  return trimBase(process.env.NEXT_PUBLIC_SUPABASE_URL);
}

// Only our own hosts: the Supabase URL and, on a custom domain, the project's
// `<ref>.supabase.co` host that earlier rows point at. A URL from another
// project (e.g. prod data in a local database) keeps loading from where it
// points.
function ownPublicPrefixes() {
  const base = getStoragePublicBaseUrl();
  if (!base) return [];

  return [base, getSupabaseProjectOrigin(base)]
    .filter((origin): origin is string => origin !== null)
    .map((origin) => `${origin}${PUBLIC_OBJECT_PATH}`);
}

function isPublicBucket(value: string): value is PublicStorageBucket {
  return (PUBLIC_STORAGE_BUCKETS as readonly string[]).includes(value);
}

/** The one gate every function goes through. */
function readRef(value: unknown): (StorageRef & { ref: string }) | null {
  // Values parsed from JSON are untyped at runtime.
  if (typeof value !== 'string' || !value) return null;

  const prefix = ownPublicPrefixes().find((p) => value.startsWith(p));
  const ref = prefix ? value.slice(prefix.length) : value;

  // A reference never carries a scheme or a leading slash.
  if (!prefix && (ref.startsWith('/') || /^[a-z][a-z\d+.-]*:/i.test(ref))) {
    return null;
  }
  if (hasUnsafeCharacter(ref)) return null;

  const slash = ref.indexOf('/');
  const bucket = ref.slice(0, slash);
  if (slash <= 0 || !isPublicBucket(bucket)) return null;

  const rest = ref.slice(slash + 1);
  const queryAt = rest.search(/[?#]/);
  const encodedPath = queryAt === -1 ? rest : rest.slice(0, queryAt);
  const query = queryAt === -1 ? '' : rest.slice(queryAt + 1).split('#')[0];

  let path: string;
  try {
    path = decodeURIComponent(encodedPath);
  } catch {
    return null;
  }
  if (hasUnsafeCharacter(path, { allowSpace: true })) return null;

  const segments = path.split('/');
  if (segments.some((s) => s === '' || s === '.' || s === '..')) return null;

  return {
    ref,
    bucket,
    path,
    version: new URLSearchParams(query).get('v'),
  };
}

/**
 * The stored reference for a reference or a legacy URL on one of our hosts;
 * null for anything else (external URLs, static paths, private buckets).
 * Legacy URLs keep their exact tail, so `prefix + ref` is the original value.
 */
export function toStorageRef(value: string | null | undefined): string | null {
  return readRef(value)?.ref ?? null;
}

/**
 * The public URL for a stored image. References and our legacy URLs are
 * served from the Supabase URL; every other value (external
 * avatars, static paths, blob/data previews, null) is returned unchanged.
 * Idempotent, never throws.
 */
export function publicStorageUrl<T extends string | null | undefined>(
  value: T,
): T {
  const ref = toStorageRef(value);
  const base = getStoragePublicBaseUrl();

  if (!ref || !base) return value;

  return `${base}${PUBLIC_OBJECT_PATH}${ref}` as T;
}

/**
 * Bucket, decoded object path and version of a reference or legacy URL, for
 * storage `remove()` / `download()`. Null when the value is not one of ours or
 * the path is malformed (empty segments, `.`/`..`, bad percent-encoding).
 */
export function parseStorageRef(
  value: string | null | undefined,
): StorageRef | null {
  const parsed = readRef(value);

  return parsed
    ? { bucket: parsed.bucket, path: parsed.path, version: parsed.version }
    : null;
}

/** What upload sites store: encoded like `getPublicUrl()`, plus `?v=`. */
export function buildStorageRef(
  bucket: PublicStorageBucket,
  path: string,
  version?: string | null,
): string {
  const ref = `${bucket}/${encodeURI(path)}`;

  return version ? `${ref}?v=${encodeURIComponent(version)}` : ref;
}
